/**
 * Radial Cluster - Observable HQ pattern implementation
 * Hierarchical circular layout
 * https://observablehq.com/@d3/radial-cluster
 */

import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { Box, Paper, Typography } from '@mui/material';
import useTokenExaminerStore from '../../stores/tokenExaminerStore';
import { getFaanaHealthColor } from '../../utils/faanaScoring';
import { getContrastText } from '../../utils/colorContrast';

// eslint-disable-next-line max-lines-per-function
function RadialCluster() {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const {
    _relationships,
    entities,
    getFilteredRelationships,
  } = useTokenExaminerStore();

  const filteredRelationships = getFilteredRelationships();

  // Update dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Render radial cluster
  // eslint-disable-next-line max-lines-per-function
  useEffect(() => {
    if (!dimensions.width || !dimensions.height || !filteredRelationships.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = dimensions.width;
    const height = dimensions.height;
    const radius = Math.min(width, height) / 2 - 50;

    const container = svg.append('g')
      .attr('transform', `translate(${width / 2},${height / 2})`);

    // Create hierarchical data structure based on entity types
    const entityTypes = ['PERSON', 'ORGANIZATION', 'PROGRAM', 'COMMUNITY'];
    const root = {
      name: 'Entities',
      children: entityTypes.map(type => ({
        name: type,
        type: 'group',
        children: Array.from(entities.values())
          .filter(entity => entity.type === type)
          .map(entity => {
            const entityRelationships = filteredRelationships.filter(r =>
              (r.sourceEntity?.crossEntityHash || r.sourceEntityHash) === entity.crossEntityHash ||
              (r.targetEntity?.crossEntityHash || r.targetEntityHash) === entity.crossEntityHash
            );

            return {
              name: entity.name,
              id: entity.crossEntityHash,
              type: entity.type,
              connectionCount: entityRelationships.length,
              avgHealth: entityRelationships.length > 0
                ? entityRelationships.reduce((sum, r) => sum + (r.overallHealth || 0), 0) / entityRelationships.length
                : 0
            };
          })
      })).filter(group => group.children.length > 0)
    };

    // Create cluster layout
    const cluster = d3.cluster()
      .size([2 * Math.PI, radius])
      .separation((a, b) => (a.parent === b.parent ? 1 : 2) / a.depth);

    const hierarchy = d3.hierarchy(root);
    cluster(hierarchy);

    // Type colors
    const typeColors = {
      PERSON: '#2196F3',
      ORGANIZATION: '#4CAF50',
      PROGRAM: '#FF9800',
      COMMUNITY: '#9C27B0',
      group: '#757575'
    };

    // Draw links
    const linkGenerator = d3.linkRadial()
      .angle(d => d.x)
      .radius(d => d.y);

    container.selectAll('.link')
      .data(hierarchy.links())
      .enter().append('path')
      .attr('class', 'link')
      .attr('d', linkGenerator)
      .attr('fill', 'none')
      .attr('stroke', '#999')
      .attr('stroke-width', d => d.target.depth === 2 ? 2 : 1)
      .attr('opacity', 0.6);

    // Add relationship links between entities
    const entityNodes = hierarchy.leaves();
    const entityLookup = new Map(entityNodes.map(d => [d.data.id, d]));

    const relationshipLinks = filteredRelationships
      .map(rel => {
        const sourceId = rel.sourceEntity?.crossEntityHash || rel.sourceEntityHash;
        const targetId = rel.targetEntity?.crossEntityHash || rel.targetEntityHash;
        const sourceNode = entityLookup.get(sourceId);
        const targetNode = entityLookup.get(targetId);

        if (sourceNode && targetNode) {
          return {
            source: sourceNode,
            target: targetNode,
            strength: rel.overallHealth || 50,
            type: rel.relationshipType
          };
        }
        return null;
      })
      .filter(Boolean);

    // Draw relationship arcs
    container.selectAll('.relationship-link')
      .data(relationshipLinks)
      .enter().append('path')
      .attr('class', 'relationship-link')
      .attr('d', d => {
        const sourceAngle = d.source.x;
        const sourceRadius = d.source.y;
        const targetAngle = d.target.x;
        const targetRadius = d.target.y;

        const sourceX = sourceRadius * Math.cos(sourceAngle - Math.PI / 2);
        const sourceY = sourceRadius * Math.sin(sourceAngle - Math.PI / 2);
        const targetX = targetRadius * Math.cos(targetAngle - Math.PI / 2);
        const targetY = targetRadius * Math.sin(targetAngle - Math.PI / 2);

        // Create curved arc
        const midX = (sourceX + targetX) / 2;
        const midY = (sourceY + targetY) / 2;
        const distance = Math.sqrt(midX * midX + midY * midY);
        const controlDistance = distance * 0.7; // Pull arc toward center
        const controlX = midX * (controlDistance / distance);
        const controlY = midY * (controlDistance / distance);

        return `M ${sourceX} ${sourceY} Q ${controlX} ${controlY} ${targetX} ${targetY}`;
      })
      .attr('fill', 'none')
      .attr('stroke', d => getFaanaHealthColor(d.strength))
      .attr('stroke-width', d => Math.max(1, d.strength / 25))
      .attr('opacity', 0.4)
      .on('mouseenter', function(event, d) {
        d3.select(this)
          .attr('opacity', 0.8)
          .attr('stroke-width', Math.max(2, d.strength / 15));
      })
      .on('mouseleave', function(event, d) {
        d3.select(this)
          .attr('opacity', 0.4)
          .attr('stroke-width', Math.max(1, d.strength / 25));
      });

    // Draw nodes
    const nodeElements = container.selectAll('.node')
      .data(hierarchy.descendants())
      .enter().append('g')
      .attr('class', 'node')
      .attr('transform', d => {
        const angle = d.x - Math.PI / 2;
        const radius = d.y;
        return `translate(${radius * Math.cos(angle)},${radius * Math.sin(angle)})`;
      });

    // Node circles
    nodeElements.append('circle')
      .attr('r', d => {
        if (d.depth === 0) return 8; // Root
        if (d.depth === 1) return 6; // Groups
        return 4 + Math.sqrt(d.data.connectionCount || 0) * 2; // Entities
      })
      .attr('fill', d => {
        if (d.depth === 0) return '#333';
        if (d.depth === 1) return typeColors[d.data.name] || '#757575';
        return d.data.avgHealth > 0 ? getFaanaHealthColor(d.data.avgHealth) : typeColors[d.data.type];
      })
      .attr('stroke', '#fff')
      .attr('stroke-width', d => d.depth === 0 ? 3 : 2)
      .on('mouseenter', function(event, d) {
        if (d.depth === 2) { // Entity nodes
          // Highlight connected relationships
          container.selectAll('.relationship-link')
            .attr('opacity', link =>
              (link.source.data.id === d.data.id || link.target.data.id === d.data.id) ? 0.8 : 0.1
            );
        }
      })
      .on('mouseleave', function() {
        container.selectAll('.relationship-link').attr('opacity', 0.4);
      });

    // Node labels
    nodeElements.append('text')
      .attr('dy', '0.31em')
      .attr('text-anchor', d => d.x < Math.PI === !d.children ? 'start' : 'end')
      .attr('transform', d => d.x >= Math.PI ? 'rotate(180)' : null)
      .style('font-size', d => {
        if (d.depth === 0) return '14px';
        if (d.depth === 1) return '12px';
        return '10px';
      })
      .style('font-weight', d => d.depth <= 1 ? 'bold' : 'normal')
      .style('fill', d => {
        if (d.depth === 0) return '#333';
        if (d.depth === 1) return typeColors[d.data.name] || '#757575';
        const bgColor = d.data.avgHealth > 0 ? getFaanaHealthColor(d.data.avgHealth) : typeColors[d.data.type];
        return getContrastText(bgColor);
      })
      .text(d => {
        if (d.depth === 0) return 'Network';
        if (d.depth === 1) return d.data.name;
        return d.data.name.length > 15 ? d.data.name.substring(0, 15) + '...' : d.data.name;
      });

    // Connection count badges for entity nodes
    nodeElements.filter(d => d.depth === 2 && d.data.connectionCount > 0)
      .append('circle')
      .attr('cx', 15)
      .attr('cy', -10)
      .attr('r', 8)
      .attr('fill', '#FF5722')
      .attr('stroke', '#fff')
      .attr('stroke-width', 1);

    nodeElements.filter(d => d.depth === 2 && d.data.connectionCount > 0)
      .append('text')
      .attr('x', 15)
      .attr('y', -6)
      .attr('text-anchor', 'middle')
      .style('font-size', '8px')
      .style('font-weight', 'bold')
      .style('fill', 'white')
      .text(d => d.data.connectionCount);

  }, [dimensions, filteredRelationships, entities]);

  return (
    <Box ref={containerRef} sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />

      {/* Legend */}
      <Paper
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          p: 2,
          maxWidth: 200,
        }}
        elevation={3}
      >
        <Typography variant="subtitle2" gutterBottom>
          Radial Cluster
        </Typography>
        <Typography variant="caption" display="block" gutterBottom>
          • Inner ring: Entity types
        </Typography>
        <Typography variant="caption" display="block" gutterBottom>
          • Outer ring: Individual entities
        </Typography>
        <Typography variant="caption" display="block">
          • Curved lines: Relationships
        </Typography>
      </Paper>
    </Box>
  );
}

export default RadialCluster;